// ============================================================
// EMS CMDB — Engagement Management Components
// Save as: src/EngagementComponents.jsx
// ============================================================

import { useState, useEffect, useCallback, useMemo } from "react";

// ============================================================
// SERVICE FLAGS DEFINITION
// Single source of truth for all service checkboxes
// ============================================================

export const SERVICE_GROUPS = [
  {
    group: "ERP & Database Systems",
    icon: "🏛️",
    services: [
      { key: "svc_erp_banner",    label: "Banner ERP" },
      { key: "svc_erp_colleague", label: "Colleague ERP" },
      { key: "svc_erp_other",     label: "Other ERP" },
    ]
  },
  {
    group: "Platform Administration",
    icon: "🖥️",
    services: [
      { key: "svc_platform_linux",   label: "Linux Server Admin" },
      { key: "svc_platform_windows", label: "Windows Server Admin" },
    ]
  },
  {
    group: "Database Administration",
    icon: "🗄️",
    services: [
      { key: "svc_dba_oracle",   label: "Oracle DBA" },
      { key: "svc_dba_sql",      label: "SQL Server DBA" },
      { key: "svc_dba_postgres", label: "PostgreSQL DBA" },
      { key: "svc_dba_mysql",    label: "MySQL DBA" },
      { key: "svc_dba_other",    label: "Other DBA" },
    ]
  },
  {
    group: "Security",
    icon: "🛡️",
    services: [
      { key: "svc_crowdstrike", label: "CrowdStrike Endpoint Protection" },
    ]
  },
  {
    group: "Monitoring",
    icon: "📡",
    services: [
      { key: "svc_netdata", label: "Netdata Monitoring" },
    ]
  },
  {
    group: "Network Services",
    icon: "🔗",
    services: [
      { key: "svc_network_engineering",    label: "Network Engineering" },
      { key: "svc_network_administration", label: "Network Administration" },
    ]
  },
];

// All service boolean keys for easy iteration
export const ALL_SERVICE_KEYS = SERVICE_GROUPS.flatMap(g => g.services.map(s => s.key));

// Default form values for a new engagement
export const DEFAULT_ENGAGEMENT = {
  customer_id: "",
  start_date: new Date().toISOString().split("T")[0],
  end_date: "",
  contract_value: "",
  contract_notes: "",
  lead_consultant: "",
  supporting_consultants: "",
  notes: "",
  svc_erp_banner: false, svc_erp_colleague: false, svc_erp_other: false,
  svc_platform_linux: false, svc_platform_windows: false,
  svc_dba_oracle: false, svc_dba_sql: false, svc_dba_postgres: false,
  svc_dba_mysql: false, svc_dba_other: false,
  svc_crowdstrike: false, svc_netdata: false,
  svc_network_engineering: false, svc_network_administration: false,
  svc_custom_1_label: "", svc_custom_1: false,
  svc_custom_2_label: "", svc_custom_2: false,
  svc_custom_3_label: "", svc_custom_3: false,
};

// ============================================================
// CSV TEMPLATE GENERATION & PARSING
// ============================================================

const ENGAGEMENT_CSV_HEADERS = [
  "customer_id", "start_date", "end_date", "contract_value",
  "lead_consultant", "supporting_consultants",
  ...ALL_SERVICE_KEYS,
  "svc_custom_1_label", "svc_custom_1",
  "svc_custom_2_label", "svc_custom_2",
  "svc_custom_3_label", "svc_custom_3",
  "contract_notes", "notes",
];

export function downloadEngagementTemplate() {
  const exampleRow = {
    customer_id: "CMI-00001",
    start_date: "2025-01-01",
    end_date: "2025-12-31",
    contract_value: "75000",
    lead_consultant: "Jane Smith",
    supporting_consultants: "Bob Jones, Alice Lee",
    svc_erp_banner: "Y", svc_erp_colleague: "N", svc_erp_other: "N",
    svc_platform_linux: "Y", svc_platform_windows: "N",
    svc_dba_oracle: "N", svc_dba_sql: "Y", svc_dba_postgres: "N",
    svc_dba_mysql: "N", svc_dba_other: "N",
    svc_crowdstrike: "Y", svc_netdata: "Y",
    svc_network_engineering: "N", svc_network_administration: "N",
    svc_custom_1_label: "Disaster Recovery", svc_custom_1: "Y",
    svc_custom_2_label: "", svc_custom_2: "N",
    svc_custom_3_label: "", svc_custom_3: "N",
    contract_notes: "Annual renewal — 5% increase",
    notes: "",
  };

  const lines = [
    ENGAGEMENT_CSV_HEADERS.join(","),
    ENGAGEMENT_CSV_HEADERS.map(h => {
      const v = exampleRow[h] ?? "";
      return String(v).includes(",") ? `"${v}"` : v;
    }).join(",")
  ];

  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "EMS_Engagements_Template.csv";
  a.click();
  URL.revokeObjectURL(a.href);
}

export function downloadCustomerTemplate() {
  const headers = [
    "customer_id", "customer_name", "service_category", "service_type",
    "erp_platform", "environment_type", "hosting_model", "region",
    "city", "state", "country",
    "primary_technical_owner", "primary_consultant", "account_manager", "contract_owner",
    "account_status", "domain", "active", "notes"
  ];
  const example = {
    customer_id: "CMI-00001",
    customer_name: "Acme University",
    service_category: "Technology Management",
    service_type: "Technology Management",
    erp_platform: "Banner",
    environment_type: "Prod",
    hosting_model: "Cloud",
    region: "Northeast",
    city: "Boston", state: "MA", country: "USA",
    primary_technical_owner: "John Doe",
    primary_consultant: "Jane Smith",
    account_manager: "Bob Jones",
    contract_owner: "",
    account_status: "Active",
    domain: "acme.edu",
    active: "Yes",
    notes: "",
  };
  const lines = [
    headers.join(","),
    headers.map(h => example[h] ?? "").join(",")
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "EMS_Customers_Template.csv";
  a.click();
  URL.revokeObjectURL(a.href);
}

// Parse Y/N string from CSV to boolean
function parseYN(val) {
  if (typeof val === "boolean") return val;
  return String(val || "").trim().toUpperCase() === "Y";
}

export function parseEngagementCSV(rows) {
  return rows.map(row => {
    const eng = { ...row };
    ALL_SERVICE_KEYS.forEach(k => { eng[k] = parseYN(row[k]); });
    ["svc_custom_1", "svc_custom_2", "svc_custom_3"].forEach(k => { eng[k] = parseYN(row[k]); });
    if (eng.contract_value) eng.contract_value = parseFloat(eng.contract_value) || null;
    if (!eng.end_date) eng.end_date = null;
    return eng;
  });
}

// ============================================================
// SERVICE FLAGS DISPLAY — compact badge list
// ============================================================

export function ServiceBadges({ engagement, compact = false }) {
  const active = SERVICE_GROUPS.flatMap(g =>
    g.services.filter(s => engagement[s.key]).map(s => ({ ...s, icon: g.icon }))
  );
  const customs = [1, 2, 3]
    .filter(n => engagement[`svc_custom_${n}`] && engagement[`svc_custom_${n}_label`])
    .map(n => ({ key: `svc_custom_${n}`, label: engagement[`svc_custom_${n}_label`], icon: "⚙️" }));
  const all = [...active, ...customs];

  if (!all.length) return <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", color: "var(--text3)" }}>None</span>;

  if (compact) {
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
        {all.map(s => (
          <span key={s.key} className="badge badge-blue" title={s.label}>{s.icon}</span>
        ))}
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.72rem", color: "var(--text2)" }}>{all.length} service{all.length !== 1 ? "s" : ""}</span>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
      {all.map(s => (
        <span key={s.key} className="badge badge-blue">{s.icon} {s.label}</span>
      ))}
    </div>
  );
}

// ============================================================
// SERVICE FLAGS EDITOR — checkbox grid for forms
// ============================================================

export function ServiceFlagsEditor({ form, setForm }) {
  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem" }}>
      {SERVICE_GROUPS.map(group => (
        <div key={group.group} style={{
          background: "var(--bg)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          padding: "1rem",
        }}>
          <div style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.7rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "var(--text2)",
            marginBottom: "0.65rem",
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
          }}>
            {group.icon} {group.group}
          </div>
          {group.services.map(svc => (
            <label key={svc.key} style={{
              display: "flex",
              alignItems: "center",
              gap: "0.6rem",
              padding: "0.3rem 0",
              cursor: "pointer",
              fontSize: "0.86rem",
              color: form[svc.key] ? "var(--text)" : "var(--text2)",
              borderBottom: "1px solid var(--border)",
            }}>
              <input
                type="checkbox"
                checked={!!form[svc.key]}
                onChange={e => set(svc.key, e.target.checked)}
                style={{ accentColor: "var(--accent)", width: 15, height: 15, cursor: "pointer" }}
              />
              {svc.label}
            </label>
          ))}
        </div>
      ))}

      {/* Custom services */}
      <div style={{
        background: "var(--bg)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius)",
        padding: "1rem",
        gridColumn: "1 / -1",
      }}>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text2)", marginBottom: "0.65rem" }}>
          ⚙️ Custom / Other Services
        </div>
        {[1, 2, 3].map(n => (
          <div key={n} style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.5rem" }}>
            <input
              type="checkbox"
              checked={!!form[`svc_custom_${n}`]}
              onChange={e => set(`svc_custom_${n}`, e.target.checked)}
              style={{ accentColor: "var(--accent)", width: 15, height: 15, cursor: "pointer", flexShrink: 0 }}
            />
            <input
              className="form-input"
              placeholder={`Custom service ${n} name`}
              value={form[`svc_custom_${n}_label`] || ""}
              onChange={e => set(`svc_custom_${n}_label`, e.target.value)}
              style={{ flex: 1 }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// ENGAGEMENT FORM MODAL
// ============================================================

export function EngagementModal({ engagement, customers, onSave, onClose, isRenewal = false }) {
  const isEdit = !!engagement && !isRenewal;
  const [form, setForm] = useState(() => {
    if (engagement) {
      const f = { ...engagement };
      if (isRenewal) {
        f.start_date = new Date().toISOString().split("T")[0];
        f.end_date = "";
        f.contract_notes = "";
      }
      return f;
    }
    return { ...DEFAULT_ENGAGEMENT };
  });
  const [tab, setTab] = useState("details");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const serviceCount = ALL_SERVICE_KEYS.filter(k => form[k]).length +
    [1,2,3].filter(n => form[`svc_custom_${n}`]).length;

  const handleSave = async () => {
    setLoading(true);
    await onSave(form, isRenewal ? engagement?.engagement_id : null);
    setLoading(false);
  };

  const title = isRenewal
    ? `Renew: ${engagement?.engagement_id}`
    : isEdit
    ? `Edit: ${engagement?.engagement_id}`
    : "New Engagement";

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ width: 820 }}>
        <div className="modal-header">
          <div className="modal-title">{title}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Inner tab bar */}
        <div style={{ display: "flex", borderBottom: "1px solid var(--border)", padding: "0 1.5rem" }}>
          {[
            { id: "details",  label: "Contract Details" },
            { id: "services", label: `Services (${serviceCount} active)` },
            { id: "team",     label: "Delivery Team & Notes" },
          ].map(t => (
            <div key={t.id}
              className={`tab-item${tab === t.id ? " active" : ""}`}
              onClick={() => setTab(t.id)}
              style={{ fontSize: "0.84rem" }}
            >
              {t.label}
            </div>
          ))}
        </div>

        <div className="modal-body" style={{ maxHeight: "65vh", overflowY: "auto" }}>

          {/* DETAILS TAB */}
          {tab === "details" && (
            <div className="form-grid">
              <div className="form-group full">
                <label className="form-label">Customer <span className="req">*</span></label>
                <select className="form-select" value={form.customer_id} onChange={e => set("customer_id", e.target.value)} disabled={isEdit}>
                  <option value="">— Select Customer —</option>
                  {customers.map(c => (
                    <option key={c.customer_id} value={c.customer_id}>
                      {c.customer_name} ({c.customer_id}) · {c.service_type || "—"}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Start Date <span className="req">*</span></label>
                <input className="form-input" type="date" value={form.start_date || ""} onChange={e => set("start_date", e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">End Date <span style={{ color: "var(--text2)", fontWeight: 400 }}>(blank = open-ended)</span></label>
                <input className="form-input" type="date" value={form.end_date || ""} onChange={e => set("end_date", e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Contract Value ($)</label>
                <input className="form-input" type="number" min="0" step="0.01" placeholder="0.00" value={form.contract_value || ""} onChange={e => set("contract_value", e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={form.status || "Active"} onChange={e => set("status", e.target.value)}>
                  {["Active","Pending","Expired","Cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {isRenewal && (
                <div className="form-group full">
                  <div style={{ padding: "0.75rem 1rem", background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: "var(--radius)", fontFamily: "var(--font-mono)", fontSize: "0.8rem", color: "var(--text2)" }}>
                    ↻ <strong style={{ color: "var(--accent2)" }}>Renewal #{(engagement?.renewal_number || 1) + 1}</strong> — The previous engagement ({engagement?.engagement_id}) will be marked "Renewed" and closed. All service flags and team assignments will carry forward and can be modified here before saving.
                  </div>
                </div>
              )}
              <div className="form-group full">
                <label className="form-label">Contract Notes</label>
                <textarea className="form-textarea" style={{ minHeight: 60 }} placeholder="e.g. Annual renewal, 5% increase, added CrowdStrike" value={form.contract_notes || ""} onChange={e => set("contract_notes", e.target.value)} />
              </div>
            </div>
          )}

          {/* SERVICES TAB */}
          {tab === "services" && (
            <ServiceFlagsEditor form={form} setForm={setForm} />
          )}

          {/* TEAM TAB */}
          {tab === "team" && (
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Lead Consultant</label>
                <input className="form-input" type="text" value={form.lead_consultant || ""} onChange={e => set("lead_consultant", e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Supporting Consultants</label>
                <input className="form-input" type="text" placeholder="Comma-separated names" value={form.supporting_consultants || ""} onChange={e => set("supporting_consultants", e.target.value)} />
              </div>
              <div className="form-group full">
                <label className="form-label">Engagement Notes</label>
                <textarea className="form-textarea" value={form.notes || ""} onChange={e => set("notes", e.target.value)} />
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading || !form.customer_id || !form.start_date}>
            {loading ? <span className="loading-spinner" /> : isRenewal ? "↻ Create Renewal" : isEdit ? "Save Changes" : "Create Engagement"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CUSTOMER FORM MODAL (extended with engagement fields)
// ============================================================

export function CustomerModal({ customer, onSave, onClose }) {
  const isEdit = !!customer;
  const [form, setForm] = useState(customer ? { ...customer } : {
    customer_name: "", erp_platform: "", environment_type: "",
    hosting_model: "", region: "", city: "", state: "", country: "USA",
    primary_technical_owner: "", primary_consultant: "", account_manager: "",
    contract_owner: "", active: "Yes", account_status: "Active",
    domain: "", service_category: "", service_type: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-set service_category when service_type changes
  const setServiceType = (type) => {
    const category = type === "Technology Management" ? "Technology Management" : "Advisory Services";
    setForm(f => ({ ...f, service_type: type, service_category: category }));
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ width: 720 }}>
        <div className="modal-header">
          <div className="modal-title">{isEdit ? `Edit Customer: ${customer.customer_id}` : "Add Customer"}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group full">
              <label className="form-label">Customer Name <span className="req">*</span></label>
              <input className="form-input" type="text" value={form.customer_name || ""} onChange={e => set("customer_name", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Service Type <span className="req">*</span></label>
              <select className="form-select" value={form.service_type || ""} onChange={e => setServiceType(e.target.value)}>
                <option value="">— Select —</option>
                <optgroup label="Technology Management">
                  <option value="Technology Management">Technology Management</option>
                </optgroup>
                <optgroup label="Advisory Services">
                  <option value="Technology Support">Technology Support</option>
                  <option value="Technical Advisory">Technical Advisory</option>
                </optgroup>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Service Category</label>
              <input className="form-input" type="text" value={form.service_category || ""} readOnly style={{ opacity: 0.6 }} />
              <div className="input-help">Auto-set from Service Type</div>
            </div>
            <div className="form-group">
              <label className="form-label">Account Status</label>
              <select className="form-select" value={form.account_status || "Active"} onChange={e => set("account_status", e.target.value)}>
                {["Active","Inactive","Prospect","On Hold","Churned"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">ERP Platform</label>
              <select className="form-select" value={form.erp_platform || ""} onChange={e => set("erp_platform", e.target.value)}>
                <option value="">— Select —</option>
                {["Banner","Colleague","Both","Unknown"].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Environment Type</label>
              <select className="form-select" value={form.environment_type || ""} onChange={e => set("environment_type", e.target.value)}>
                <option value="">— Select —</option>
                {["Prod","Non-Prod","Mixed","Unknown"].map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Primary Consultant</label>
              <input className="form-input" type="text" value={form.primary_consultant || ""} onChange={e => set("primary_consultant", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Account Manager</label>
              <input className="form-input" type="text" value={form.account_manager || ""} onChange={e => set("account_manager", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Primary Technical Owner</label>
              <input className="form-input" type="text" value={form.primary_technical_owner || ""} onChange={e => set("primary_technical_owner", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Domain</label>
              <input className="form-input" type="text" placeholder="acme.edu" value={form.domain || ""} onChange={e => set("domain", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" type="text" value={form.city || ""} onChange={e => set("city", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input className="form-input" type="text" value={form.state || ""} onChange={e => set("state", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Hosting Model</label>
              <input className="form-input" type="text" value={form.hosting_model || ""} onChange={e => set("hosting_model", e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Region</label>
              <input className="form-input" type="text" value={form.region || ""} onChange={e => set("region", e.target.value)} />
            </div>
            <div className="form-group full">
              <label className="form-label">Notes</label>
              <textarea className="form-textarea" value={form.notes || ""} onChange={e => set("notes", e.target.value)} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={async () => { setLoading(true); await onSave(form); setLoading(false); }} disabled={loading || !form.customer_name || !form.service_type}>
            {loading ? <span className="loading-spinner" /> : isEdit ? "Save Changes" : "Add Customer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ENGAGEMENT MANAGEMENT PAGE
// ============================================================

export function EngagementPage({ supabase, profile, userEmail, toast, canManage }) {
  const [engagements, setEngagements] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);         // null | "add" | engagement object
  const [renewModal, setRenewModal] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("Active");
  const [filterType, setFilterType] = useState("All");
  const [tab, setTab] = useState("list");
  const isAdmin = profile?.role === "administrator";
  const canEdit = canManage ?? isAdmin;  // managers and admins can edit

  const load = useCallback(async () => {
    setLoading(true);
    const [engRes, custRes] = await Promise.all([
      supabase.from("active_engagements").select("*"),
      supabase.from("customers").select("*").order("customer_name"),
    ]);
    // For non-active, fetch from the full table
    const { data: allEng } = await supabase.from("engagements").select("*").order("start_date", { ascending: false });
    setEngagements(allEng || []);
    setCustomers(custRes.data || []);
    setLoading(false);
  }, [supabase]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return engagements.filter(e => {
      if (filterStatus !== "All" && e.status !== filterStatus) return false;
      if (filterType !== "All") {
        const cust = customers.find(c => c.customer_id === e.customer_id);
        if (!cust || cust.service_type !== filterType) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const cust = customers.find(c => c.customer_id === e.customer_id);
        return (
          e.engagement_id?.toLowerCase().includes(q) ||
          e.customer_id?.toLowerCase().includes(q) ||
          cust?.customer_name?.toLowerCase().includes(q) ||
          e.lead_consultant?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [engagements, customers, filterStatus, filterType, search]);

  // Expiring within 90 days
  const expiring = useMemo(() =>
    engagements.filter(e => {
      if (e.status !== "Active" || !e.end_date) return false;
      const days = Math.ceil((new Date(e.end_date) - new Date()) / 86400000);
      return days <= 90 && days >= 0;
    }).sort((a, b) => new Date(a.end_date) - new Date(b.end_date)),
    [engagements]
  );

  const handleSave = async (form, renewFromId) => {
    const userId = (await supabase.auth.getUser()).data.user?.id;

    if (renewFromId) {
      // Use the DB renewal function
      const endDate = form.end_date || null;
      const { data, error } = await supabase.rpc("renew_engagement", {
        source_engagement_id: renewFromId,
        new_start_date: form.start_date,
        new_end_date: endDate,
        new_contract_value: form.contract_value ? parseFloat(form.contract_value) : null,
        new_notes: form.notes || null,
      });
      if (error) { toast("Renewal failed: " + error.message, "error"); return; }
      // Update service flags on the new engagement
      if (data) {
        const updates = {};
        ALL_SERVICE_KEYS.forEach(k => { updates[k] = !!form[k]; });
        [1,2,3].forEach(n => {
          updates[`svc_custom_${n}`] = !!form[`svc_custom_${n}`];
          updates[`svc_custom_${n}_label`] = form[`svc_custom_${n}_label`] || null;
        });
        updates.lead_consultant = form.lead_consultant || null;
        updates.supporting_consultants = form.supporting_consultants || null;
        updates.contract_notes = form.contract_notes || null;
        await supabase.from("engagements").update(updates).eq("engagement_id", data);
      }
      toast("Renewal created successfully", "success");
      setRenewModal(null);
    } else if (form.id) {
      // Update existing
      const { error } = await supabase.from("engagements").update({ ...form, updated_by: userId }).eq("id", form.id);
      if (error) { toast("Update failed: " + error.message, "error"); return; }
      toast("Engagement updated", "success");
      setModal(null);
    } else {
      // Insert new
      const engId = `ENG-${String(Date.now()).slice(-5)}`;
      const payload = {
        ...form,
        engagement_id: engId,
        contract_value: form.contract_value ? parseFloat(form.contract_value) : null,
        end_date: form.end_date || null,
        status: "Active",
        renewal_number: 1,
        created_by: userId,
        updated_by: userId,
      };
      const { error } = await supabase.from("engagements").insert(payload);
      if (error) { toast("Insert failed: " + error.message, "error"); return; }
      toast("Engagement created", "success");
      setModal(null);
    }
    load();
  };

  const handleDelete = async (eng) => {
// eslint-disable-next-line no-restricted-globals
    if (!confirm(`Delete engagement ${eng.engagement_id}? This cannot be undone.`)) return;
    const { error } = await supabase.from("engagements").delete().eq("id", eng.id);
    if (error) toast("Delete failed: " + error.message, "error");
    else { toast("Deleted", "info"); load(); }
  };

  const getCustomer = (id) => customers.find(c => c.customer_id === id);

  const statusColor = s => ({
    Active: "badge-green", Renewed: "badge-blue", Expired: "badge-gray",
    Cancelled: "badge-red", Pending: "badge-yellow",
  }[s] || "badge-gray");

  const typeColor = t => ({
    "Technology Management": "badge-purple",
    "Technology Support": "badge-blue",
    "Technical Advisory": "badge-yellow",
  }[t] || "badge-gray");

  const daysColor = d => {
    if (d === null) return "var(--text2)";
    if (d < 0) return "var(--danger)";
    if (d <= 30) return "var(--danger)";
    if (d <= 90) return "var(--warning)";
    return "var(--success)";
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">📋 Engagements</div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={downloadEngagementTemplate}>📋 CSV Template</button>
          {canEdit && <button className="btn btn-primary" onClick={() => setModal("add")}>+ New Engagement</button>}
        </div>
      </div>

      {/* Expiry alerts */}
      {expiring.length > 0 && (
        <div style={{ margin: "0 2rem 0", padding: "0.7rem 1.25rem", background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.2)", borderRadius: "var(--radius)", fontFamily: "var(--font-mono)", fontSize: "0.8rem", color: "var(--warning)", display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
          <span>⚠️ <strong>{expiring.length}</strong> engagement{expiring.length !== 1 ? "s" : ""} expiring within 90 days —</span>
          {expiring.slice(0, 3).map(e => {
            const days = Math.ceil((new Date(e.end_date) - new Date()) / 86400000);
            const cust = getCustomer(e.customer_id);
            return <span key={e.id} style={{ color: days <= 30 ? "var(--danger)" : "var(--warning)" }}>{cust?.customer_name || e.customer_id} ({days}d)</span>;
          })}
          {expiring.length > 3 && <span>+{expiring.length - 3} more</span>}
          <button className="btn btn-warning btn-sm" style={{ marginLeft: "auto" }} onClick={() => setTab("expiring")}>View All</button>
        </div>
      )}

      <div className="page-content">
        {/* Tabs */}
        <div className="tab-bar">
          {[
            { id: "list",     label: `All Engagements (${filtered.length})` },
            { id: "expiring", label: `⏰ Expiring Soon (${expiring.length})` },
          ].map(t => (
            <div key={t.id} className={`tab-item${tab === t.id ? " active" : ""}`} onClick={() => setTab(t.id)}>{t.label}</div>
          ))}
        </div>

        {tab === "list" && (
          <>
            <div className="toolbar">
              <input className="search-input" placeholder="Search by customer, engagement ID, or consultant..." value={search} onChange={e => setSearch(e.target.value)} />
              <select className="form-select" style={{ width: "auto" }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="All">All Statuses</option>
                {["Active","Renewed","Expired","Cancelled","Pending"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className="form-select" style={{ width: "auto" }} value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="All">All Types</option>
                <option value="Technology Management">Technology Management</option>
                <option value="Technology Support">Technology Support</option>
                <option value="Technical Advisory">Technical Advisory</option>
              </select>
              <span className="record-count">{filtered.length} records</span>
            </div>

            {loading
              ? <div style={{ textAlign: "center", padding: "3rem" }}><div className="loading-spinner" style={{ width: 32, height: 32, borderWidth: 3 }} /></div>
              : filtered.length === 0
              ? <div className="empty-state"><div className="empty-icon">📋</div><div className="empty-text">No engagements found. Create one or upload a CSV.</div></div>
              : (
                <div className="table-wrap">
                  <table>
                    <thead><tr>
                      <th>ID</th>
                      <th>Customer</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Days Left</th>
                      <th>Services</th>
                      <th>Lead</th>
                      <th>Renewal #</th>
                      <th>Actions</th>
                    </tr></thead>
                    <tbody>
                      {filtered.map(e => {
                        const cust = getCustomer(e.customer_id);
                        const days = e.end_date ? Math.ceil((new Date(e.end_date) - new Date()) / 86400000) : null;
                        return (
                          <tr key={e.id}>
                            <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem" }}>{e.engagement_id}</td>
                            <td style={{ maxWidth: 180 }}>{cust?.customer_name || e.customer_id}</td>
                            <td><span className={`badge ${typeColor(cust?.service_type)}`}>{cust?.service_type || "—"}</span></td>
                            <td><span className={`badge ${statusColor(e.status)}`}>{e.status}</span></td>
                            <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem" }}>{e.start_date}</td>
                            <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem" }}>{e.end_date || <span style={{ color: "var(--text3)" }}>Open</span>}</td>
                            <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.8rem", fontWeight: 600, color: daysColor(days) }}>
                              {days === null ? "—" : days < 0 ? `${Math.abs(days)}d ago` : `${days}d`}
                            </td>
                            <td><ServiceBadges engagement={e} compact /></td>
                            <td style={{ maxWidth: 120 }}>{e.lead_consultant || "—"}</td>
                            <td style={{ textAlign: "center" }}>
                              {e.renewal_number > 1
                                ? <span className="badge badge-purple">#{e.renewal_number}</span>
                                : <span style={{ color: "var(--text3)" }}>Original</span>}
                            </td>
                            <td>
                              <div className="td-actions">
                                {canEdit && <button className="btn btn-secondary btn-sm" onClick={() => setModal(e)}>Edit</button>}
                                {canEdit && e.status === "Active" && <button className="btn btn-success btn-sm" onClick={() => setRenewModal(e)}>↻ Renew</button>}
                                {isAdmin && <button className="btn btn-danger btn-sm" onClick={() => handleDelete(e)}>Del</button>}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
          </>
        )}

        {tab === "expiring" && (
          <>
            {expiring.length === 0
              ? <div style={{ textAlign: "center", padding: "3rem", color: "var(--success)", fontFamily: "var(--font-mono)" }}>✓ No engagements expiring within 90 days</div>
              : (
                <div className="table-wrap">
                  <table>
                    <thead><tr>
                      <th>Customer</th>
                      <th>Type</th>
                      <th>Engagement ID</th>
                      <th>End Date</th>
                      <th>Days Remaining</th>
                      <th>Lead</th>
                      <th>Value</th>
                      <th>Actions</th>
                    </tr></thead>
                    <tbody>
                      {expiring.map(e => {
                        const cust = getCustomer(e.customer_id);
                        const days = Math.ceil((new Date(e.end_date) - new Date()) / 86400000);
                        return (
                          <tr key={e.id}>
                            <td>{cust?.customer_name || e.customer_id}</td>
                            <td><span className={`badge ${typeColor(cust?.service_type)}`}>{cust?.service_type || "—"}</span></td>
                            <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem" }}>{e.engagement_id}</td>
                            <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.76rem" }}>{e.end_date}</td>
                            <td>
                              <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: "0.9rem", color: daysColor(days) }}>
                                {days}d
                              </span>
                            </td>
                            <td>{e.lead_consultant || "—"}</td>
                            <td style={{ fontFamily: "var(--font-mono)", fontSize: "0.8rem" }}>
                              {e.contract_value ? `$${Number(e.contract_value).toLocaleString()}` : "—"}
                            </td>
                            <td>
                              <div className="td-actions">
                                {canEdit && <button className="btn btn-success btn-sm" onClick={() => setRenewModal(e)}>↻ Renew</button>}
                                {canEdit && <button className="btn btn-secondary btn-sm" onClick={() => setModal(e)}>Edit</button>}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
          </>
        )}
      </div>

      {modal && (
        <EngagementModal
          engagement={modal === "add" ? null : modal}
          customers={customers}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
      {renewModal && (
        <EngagementModal
          engagement={renewModal}
          customers={customers}
          onSave={handleSave}
          onClose={() => setRenewModal(null)}
          isRenewal={true}
        />
      )}
    </div>
  );
}
